import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ==================== LEVEL API FUNCTIONS ====================

/**
 * Get list of all levels (Authenticated users)
 * @returns {Promise} Array of levels
 */
export const getAllLevels = async () => {
    try {
        const response = await api.get('/levels/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single level by id (server-side filtered via the list endpoint -- there's no GET on the
 * level detail route).
 * @param {number} levelId - Level ID to fetch
 * @returns {Promise<Object|null>} The level, or null if not found
 */
export const getLevelById = async (levelId) => {
    try {
        const response = await api.get(`/levels/?id=${levelId}`);
        return response.data[0] || null;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new level (Admin only)
 * @param {Object} levelData - Level data object
 * @param {string} levelData.name - Level name
 * @param {string} levelData.description - Level description
 * @param {string} levelData.type - Level type (Kindergarten, Elementary, etc.)
 * @param {File} levelData.icon - Level icon image (optional)
 * @returns {Promise} Created level data
 */
export const createLevel = async (levelData) => {
    try {
        const config = {};
        let data = levelData;

        if (levelData?.icon instanceof File || levelData?.icon instanceof Blob) {
            const formData = new FormData();
            Object.keys(levelData).forEach(key => {
                if (levelData[key] !== null && levelData[key] !== undefined) {
                    formData.append(key, levelData[key]);
                }
            });
            data = formData;
        }
        
        const response = await api.post('/levels/', data, config);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing level (Admin only)
 * @param {number} levelId - Level ID
 * @param {Object} levelData - Updated level data
 * @returns {Promise} Updated level data
 */
export const updateLevel = async (levelId, levelData) => {
    try {
        const config = {};
        let data = levelData;

        if (levelData?.icon instanceof File || levelData?.icon instanceof Blob) {
            const formData = new FormData();
            Object.keys(levelData).forEach(key => {
                if (levelData[key] !== null && levelData[key] !== undefined) {
                    formData.append(key, levelData[key]);
                }
            });
            data = formData;
        }
        
        const response = await api.patch(`/levels/${levelId}/`, data, config);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a level (Admin only)
 * @param {number} levelId - Level ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteLevel = async (levelId) => {
    try {
        const response = await api.delete(`/levels/${levelId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Level Domains

export const getLevelDomains = async () => {
    try {
        const response = await api.get(`/levels/domains/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createLevelDomain = async (domainData) => {
    try {
        const response = await api.post(`/levels/domains/`, domainData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateLevelDomain = async (domainId, domainData) => {
    try {
        const response = await api.patch(`/levels/domains/${domainId}/`, domainData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}

export const deleteLevelDomain = async (domainId) => {
    try {
        const response =  await api.delete(`/levels/domains/${domainId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}
